import React, { useEffect, useState } from 'react';
import { CandleSize, Timeframe } from '../types/StockData';

interface LdpmParameterSelectorProps {
  candleSize: CandleSize;
  timeframe: Timeframe;
  onCandleSizeChange: (size: CandleSize) => void;
  onTimeframeChange: (timeframe: Timeframe) => void;
  // New: integrate ticker search + refresh in this control card
  currentTicker: string;
  onTickerSearch: (ticker: string) => void;
  onRefresh: () => void;
  isLoading?: boolean;
}

const LdpmParameterSelector: React.FC<LdpmParameterSelectorProps> = ({
  candleSize,
  timeframe,
  onCandleSizeChange,
  onTimeframeChange,
  currentTicker,
  onTickerSearch,
  onRefresh,
  isLoading = false,
}) => {
  const [inputTicker, setInputTicker] = useState<string>(currentTicker);
  useEffect(() => {
    setInputTicker(currentTicker);
  }, [currentTicker]);
  const candleSizeOptions: { value: CandleSize; label: string }[] = [
    { value: 5, label: '5m' },
    { value: 15, label: '15m' },
    { value: 30, label: '30m' },
    { value: 60, label: '1h' },
    { value: 240, label: '4h' }
  ];

  const timeframeOptions: { value: Timeframe; label: string }[] = [
    { value: '1W', label: '1 Week' },
    { value: '2W', label: '2 Weeks' },
    { value: '1M', label: '1 Month' },
    { value: '2M', label: '2 Months' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Chart Parameters</h3>

      {/* Integrated Ticker Search + Refresh */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Ticker</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={inputTicker}
              onChange={(e) => setInputTicker(e.target.value.toUpperCase())}
              placeholder="Enter ticker..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); inputTicker.trim() && onTickerSearch(inputTicker.trim()); } }}
            />
            <button
              onClick={() => inputTicker.trim() && onTickerSearch(inputTicker.trim())}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isLoading ? 'bg-blue-300 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            >
              Search
            </button>
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isLoading ? 'bg-green-300 text-white' : 'bg-green-600 text-white hover:bg-green-700'}`}
              title="Refresh data"
            >
              <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-1">Currently viewing: <span className="font-medium">{currentTicker}</span></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Candle Size Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Candle Size
          </label>
          <div className="grid grid-cols-5 gap-2">
            {candleSizeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => onCandleSizeChange(option.value)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  candleSize === option.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Timeframe Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timeframe
          </label>
          <div className="grid grid-cols-2 gap-2">
            {timeframeOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => onTimeframeChange(option.value)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  timeframe === option.value
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LdpmParameterSelector;
